import { useLicense } from '../../../hooks/useLicense';
import { LicenseGatedButton } from '../../../components/LicenseBlock';
import React, { useState, useEffect, useCallback } from 'react';
import {
  FileSignature, Plus, Download, Send, Eye, Trash2, Edit3,
  Clock, CheckCircle, AlertCircle, XCircle,
  Building2, User, Calendar, FileText, Search, RefreshCw,
  ArrowRight, Sparkles, Mail, MousePointer2, X, Phone, Save
} from 'lucide-react';
import { toast, translateBackendError } from '../../../utils/toastUtils';
import { contracts as contractsApi } from '../../../services/api/client';
import CreateContractDialog from './CreateContractDialog';
import ContractPreviewDialog from './ContractPreviewDialog';
import { useI18n } from '../../../i18n';

// Edit Contract Dialog Component
const EditContractDialog = ({ contract, onClose, onUpdated }) => {
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    client_contact_phone: contract.client_contact_phone || '',
    client_contact_email: contract.client_contact_email || '',
    client_representative_name: contract.client_representative_name || '',
    client_representative_function: contract.client_representative_function || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await contractsApi.update(contract.id, formData);
      toast.success('Contrat mis à jour');
      onUpdated(result.contract);
      onClose();
    } catch (error) {
      toast.error(translateBackendError(error.response?.data?.detail) || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-brand-darker border border-gray-700 rounded-2xl max-w-md w-full overflow-hidden">
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Edit3 className="w-6 h-6 text-brand-green" />
              <h3 className="text-lg font-semibold text-white">Modifier le contrat</h3>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/10">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-gray-400 mt-2">{contract.contract_number}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <User className="w-4 h-4 inline mr-2" />
              Nom du signataire
            </label>
            <input
              type="text"
              value={formData.client_representative_name}
              onChange={(e) => setFormData({ ...formData, client_representative_name: e.target.value })}
              className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-brand-green"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Building2 className="w-4 h-4 inline mr-2" />
              Fonction
            </label>
            <input
              type="text"
              value={formData.client_representative_function}
              onChange={(e) => setFormData({ ...formData, client_representative_function: e.target.value })}
              className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-brand-green"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Mail className="w-4 h-4 inline mr-2" />
              E-mail de contact
            </label>
            <input
              type="email"
              value={formData.client_contact_email}
              onChange={(e) => setFormData({ ...formData, client_contact_email: e.target.value })}
              placeholder={t('auth.emailPlaceholder')}
              className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-brand-green"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Phone className="w-4 h-4 inline mr-2" />
              Téléphone (pour SMS de validation)
            </label>
            <input
              type="tel"
              value={formData.client_contact_phone}
              onChange={(e) => setFormData({ ...formData, client_contact_phone: e.target.value })}
              placeholder="+33 6 12 34 56 78"
              className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-brand-green"
            />
            <p className="text-xs text-gray-500 mt-1">Format international avec indicatif pays (ex: +33 pour France)</p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-brand-green hover:bg-brand-green/90 text-black rounded-lg font-medium disabled:opacity-50"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ContractsTab = ({ workspace, onRefresh }) => {
  const [contracts, setContracts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  // Guide dialog for first contract
  const [showGuideDialog, setShowGuideDialog] = useState(false);
  const [newlyCreatedContract, setNewlyCreatedContract] = useState(null);
  const [highlightSendButton, setHighlightSendButton] = useState(null);
  const [pendingDraftContract, setPendingDraftContract] = useState(null);

  // Fetch contracts and templates
  const fetchData = useCallback(async () => {
    if (!workspace?.id) return;

    setLoading(true);
    try {
      const [contractsList, templatesList] = await Promise.all([
        contractsApi.list(workspace.id).catch(() => []),
        contractsApi.listTemplates(workspace.id).catch(() => [])
      ]);

      setContracts(contractsList || []);
      setTemplates(templatesList || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast.error('Erreur lors du chargement des contrats');
    } finally {
      setLoading(false);
    }
  }, [workspace?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check for pending draft contracts and show guide
  useEffect(() => {
    if (loading || contracts.length === 0) return;

    // Find draft contracts that haven't been sent
    const draftContracts = contracts.filter(c => c.status === 'draft');

    if (draftContracts.length > 0) {
      // Check if user has dismissed the guide for this workspace
      const dismissedKey = `contract_guide_dismissed_${workspace?.id}`;
      const dismissed = localStorage.getItem(dismissedKey);

      if (!dismissed) {
        // Show guide for the first draft contract
        setPendingDraftContract(draftContracts[0]);
        setShowGuideDialog(true);
      } else {
        // Still highlight the send button if there are drafts
        setHighlightSendButton(draftContracts[0].id);
        setTimeout(() => setHighlightSendButton(null), 3000);
      }
    }
  }, [loading, contracts, workspace?.id]);

  // Filter contracts
  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = searchQuery === '' ||
      contract.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contract.contract_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contract.client_name?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || contract.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Handle contract creation
  const handleContractCreated = (newContract) => {
    setContracts(prev => [newContract, ...prev]);
    setShowCreateDialog(false);
    // Show guide dialog for sending the contract
    setNewlyCreatedContract(newContract);
    setShowGuideDialog(true);
  };

  // Handle guide dialog close and highlight send button
  const handleGuideClose = (sendNow = false) => {
    setShowGuideDialog(false);
    const contract = newlyCreatedContract || pendingDraftContract;

    // Mark as dismissed for this workspace
    if (workspace?.id) {
      localStorage.setItem(`contract_guide_dismissed_${workspace.id}`, 'true');
    }

    if (sendNow && contract) {
      // Highlight the send button with animation
      setHighlightSendButton(contract.id);
      setTimeout(() => {
        setHighlightSendButton(null);
      }, 5000); // Remove highlight after 5 seconds
    }
    setNewlyCreatedContract(null);
    setPendingDraftContract(null);
  };

  // Handle send from guide dialog
  const handleSendFromGuide = async () => {
    const contract = newlyCreatedContract || pendingDraftContract;
    if (!contract) return;
    setShowGuideDialog(false);
    await handleSendForSignature(contract.id);
    setNewlyCreatedContract(null);
    setPendingDraftContract(null);
  };

  // Send contract for signature
  const handleSendForSignature = async (contractId) => {
    setActionLoading(prev => ({ ...prev, [contractId]: 'sending' }));
    try {
      await contractsApi.send(contractId);
      toast.success('Contrat envoyé pour signature');
      fetchData();
    } catch (error) {
      toast.error(translateBackendError(error.response?.data?.detail) || 'Erreur lors de l\'envoi');
    } finally {
      setActionLoading(prev => ({ ...prev, [contractId]: null }));
    }
  };

  // Download PDF
  const handleDownloadPdf = async (contractId, contractNumber) => {
    setActionLoading(prev => ({ ...prev, [contractId]: 'downloading' }));
    try {
      const blob = await contractsApi.downloadPdf(contractId);

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrat-${contractNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PDF');
    } finally {
      setActionLoading(prev => ({ ...prev, [contractId]: null }));
    }
  };

  // Delete contract
  const handleDeleteContract = async (contractId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contrat ?')) return;

    setActionLoading(prev => ({ ...prev, [contractId]: 'deleting' }));
    try {
      await contractsApi.delete(contractId);
      setContracts(prev => prev.filter(c => c.id !== contractId));
      toast.success('Contrat supprimé');
    } catch (error) {
      toast.error(translateBackendError(error.response?.data?.detail) || 'Erreur lors de la suppression');
    } finally {
      setActionLoading(prev => ({ ...prev, [contractId]: null }));
    }
  };

  // View contract
  const handleViewContract = (contract) => {
    setSelectedContract(contract);
    setShowPreviewDialog(true);
  };

  // Edit contract
  const handleEditContract = (contract) => {
    setSelectedContract(contract);
    setShowEditDialog(true);
  };

  // Handle contract update
  const handleContractUpdated = (updatedContract) => {
    setContracts(prev => prev.map(c => c.id === updatedContract.id ? updatedContract : c));
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      draft: { label: 'Brouillon', color: 'bg-gray-500', icon: FileText },
      pending_signature: { label: 'En attente', color: 'bg-yellow-500', icon: Clock },
      signed: { label: 'Signé', color: 'bg-green-500', icon: CheckCircle },
      active: { label: 'Actif', color: 'bg-blue-500', icon: CheckCircle },
      completed: { label: 'Terminé', color: 'bg-purple-500', icon: CheckCircle },
      expired: { label: 'Expiré', color: 'bg-red-500', icon: AlertCircle },
      cancelled: { label: 'Annulé', color: 'bg-gray-600', icon: XCircle }
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-brand-green" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileSignature className="w-6 h-6 text-brand-green" />
          <h2 className="text-lg font-semibold text-white">Contrats d'autorisation</h2>
          <span className="px-2 py-0.5 bg-gray-700 rounded-full text-xs text-gray-300">
            {contracts.length}
          </span>
        </div>
        <LicenseGatedButton
          onClick={() => setShowCreateDialog(true)}
          feature="les contrats"
          className="flex items-center gap-2 px-4 py-2 bg-brand-green hover:bg-brand-green-light text-black rounded-lg transition-colors font-medium"
        >
          <Plus className="w-4 h-4" />
          Nouveau contrat
        </LicenseGatedButton>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher par titre, numéro ou client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-brand-green"
        >
          <option value="all">Tous les statuts</option>
          <option value="draft">Brouillon</option>
          <option value="pending_signature">En attente de signature</option>
          <option value="signed">Signé</option>
          <option value="active">Actif</option>
          <option value="completed">Terminé</option>
          <option value="expired">Expiré</option>
          <option value="cancelled">Annulé</option>
        </select>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Contracts list */}
      {filteredContracts.length === 0 ? (
        <div className="text-center py-12 bg-brand-black/50 rounded-lg border border-gray-700">
          <FileSignature className="w-12 h-12 mx-auto text-gray-500 mb-4" />
          <p className="text-gray-400 mb-4">
            {searchQuery || statusFilter !== 'all'
              ? 'Aucun contrat ne correspond à vos critères'
              : 'Aucun contrat d\'autorisation'}
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <button
              onClick={() => setShowCreateDialog(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green hover:bg-brand-green-light text-black rounded-lg transition-colors font-medium"
            >
              <Plus className="w-4 h-4" />
              Créer votre premier contrat
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredContracts.map((contract) => (
            <div
              key={contract.id}
              className="bg-brand-black rounded-lg border border-gray-700 p-4 hover:border-brand-green/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-white font-medium truncate">{contract.title}</h3>
                    <StatusBadge status={contract.status} />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-gray-400">
                      <FileText className="w-4 h-4" />
                      <span>{contract.contract_number}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <Building2 className="w-4 h-4" />
                      <span className="truncate capitalize">{contract.client_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <User className="w-4 h-4" />
                      <span className="truncate">{contract.client_representative_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {new Date(contract.start_date).toLocaleDateString('fr-FR')} - {new Date(contract.end_date).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>

                  {contract.signed_at && (
                    <div className="mt-2 text-xs text-green-400">
                      Signé le {new Date(contract.signed_at).toLocaleDateString('fr-FR')} à {new Date(contract.signed_at).toLocaleTimeString('fr-FR')}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleViewContract(contract)}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                    title="Voir le contrat"
                  >
                    <Eye className="w-4 h-4" />
                  </button>

                  {contract.status === 'signed' && (
                    <button
                      onClick={() => handleDownloadPdf(contract.id, contract.contract_number)}
                      disabled={actionLoading[contract.id] === 'downloading'}
                      className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                      title="Télécharger le PDF signé"
                    >
                      {actionLoading[contract.id] === 'downloading' ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                    </button>
                  )}

                  {(contract.status === 'draft' || contract.status === 'pending_signature') && (
                    <button
                      onClick={() => handleEditContract(contract)}
                      className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30 rounded-lg transition-colors"
                      title="Modifier"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                  )}

                  {contract.status === 'draft' && (
                    <>
                      <LicenseGatedButton
                        onClick={() => handleSendForSignature(contract.id)}
                        disabled={actionLoading[contract.id] === 'sending'}
                        feature="la signature de contrats"
                        className={`p-2 rounded-lg transition-all disabled:opacity-50 ${
                          highlightSendButton === contract.id
                            ? 'bg-brand-green text-black animate-pulse ring-4 ring-brand-green/50 scale-110'
                            : 'text-brand-green hover:text-brand-green-light hover:bg-brand-green/10'
                        }`}
                        title="Envoyer pour signature"
                      >
                        {actionLoading[contract.id] === 'sending' ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </LicenseGatedButton>
                    </>
                  )}

                  {(contract.status === 'draft' || contract.status === 'pending_signature') && (
                    <button
                      onClick={() => handleDeleteContract(contract.id)}
                      disabled={actionLoading[contract.id] === 'deleting'}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50"
                      title="Supprimer"
                    >
                      {actionLoading[contract.id] === 'deleting' ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Contract Dialog */}
      {showCreateDialog && (
        <CreateContractDialog
          workspace={workspace}
          templates={templates}
          onClose={() => setShowCreateDialog(false)}
          onCreated={handleContractCreated}
        />
      )}

      {/* Preview Contract Dialog */}
      {showPreviewDialog && selectedContract && (
        <ContractPreviewDialog
          contract={selectedContract}
          onClose={() => {
            setShowPreviewDialog(false);
            setSelectedContract(null);
          }}
        />
      )}

      {/* Edit Contract Dialog */}
      {showEditDialog && selectedContract && (
        <EditContractDialog
          contract={selectedContract}
          onClose={() => {
            setShowEditDialog(false);
            setSelectedContract(null);
          }}
          onUpdated={handleContractUpdated}
        />
      )}

      {/* Guide Dialog after contract creation or for pending drafts */}
      {showGuideDialog && (newlyCreatedContract || pendingDraftContract) && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-brand-darker border border-brand-green/30 rounded-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            {/* Header with success animation */}
            <div className={`bg-gradient-to-r ${newlyCreatedContract ? 'from-brand-green/20 to-blue-500/20' : 'from-yellow-500/20 to-orange-500/20'} p-6 border-b border-brand-green/20`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-full ${newlyCreatedContract ? 'bg-brand-green/20' : 'bg-yellow-500/20'} flex items-center justify-center animate-bounce`}>
                    {newlyCreatedContract ? (
                      <CheckCircle className="w-8 h-8 text-brand-green" />
                    ) : (
                      <AlertCircle className="w-8 h-8 text-yellow-400" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      {newlyCreatedContract ? 'Contrat créé avec succès !' : 'Contrat en attente d\'envoi'}
                    </h3>
                    <p className="text-gray-400 text-sm">{(newlyCreatedContract || pendingDraftContract)?.contract_number}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleGuideClose(false)}
                  className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Info box */}
              <div className="bg-brand-black rounded-xl p-4 border border-gray-700">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium mb-1">
                      {newlyCreatedContract ? 'Prochaine étape importante' : 'Action requise'}
                    </p>
                    <p className="text-gray-400 text-sm">
                      {newlyCreatedContract
                        ? 'Votre contrat est prêt ! Pour qu\'il soit valide, il doit être signé par le client.'
                        : 'Vous avez un contrat en brouillon qui n\'a pas encore été envoyé au client pour signature.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Steps */}
              <div className="space-y-4">
                <h4 className="text-white font-medium flex items-center gap-2">
                  <Mail className="w-5 h-5 text-brand-green" />
                  Comment envoyer le contrat ?
                </h4>

                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-brand-green/10 rounded-lg border border-brand-green/20">
                    <div className="w-6 h-6 rounded-full bg-brand-green text-black flex items-center justify-center text-sm font-bold flex-shrink-0">
                      1
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Cliquez sur le bouton "Envoyer"</p>
                      <p className="text-gray-400 text-xs">Le bouton <Send className="w-3 h-3 inline mx-1 text-brand-green" /> à côté du contrat</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      2
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Un email sera envoyé au client</p>
                      <p className="text-gray-400 text-xs">À l'adresse : {(newlyCreatedContract || pendingDraftContract)?.client_contact_email}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                    <div className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      3
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Le client signe en ligne</p>
                      <p className="text-gray-400 text-xs">Signature électronique sécurisée et légalement valide</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Visual indicator */}
              <div className="flex items-center justify-center gap-2 text-gray-400 text-sm">
                <MousePointer2 className="w-4 h-4 animate-bounce" />
                <span>Cliquez ci-dessous pour envoyer maintenant</span>
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 bg-brand-black/50 border-t border-gray-800 flex items-center justify-between gap-4">
              <button
                onClick={() => handleGuideClose(true)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Plus tard
              </button>
              <button
                onClick={handleSendFromGuide}
                className="flex items-center gap-2 px-6 py-3 bg-brand-green hover:bg-brand-green/90 text-black rounded-xl font-semibold transition-all hover:scale-105 shadow-lg shadow-brand-green/20"
              >
                <Send className="w-5 h-5" />
                Envoyer le contrat maintenant
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractsTab;
